/**
 * Chart primitives shared by every visualization.
 *
 * The series order is fixed and never cycled: slot N always means the same
 * entity for a given dataset, so a filter that removes a series cannot repaint
 * the survivors. Anything past the eighth slot folds into "Other" rather than
 * generating a ninth hue.
 */

export const SERIES_SLOTS = 8;

/** CSS custom property for a series slot, by index (0-based). */
export function seriesVar(index) {
  if (index >= SERIES_SLOTS) return 'var(--series-other)';
  return `var(--series-${index + 1})`;
}

/**
 * Fold a ranked list to at most `limit` entries plus an aggregated "Other".
 * Keeps identity stable: entries stay in rank order and the fold is the tail.
 */
export function foldToOther(items, { limit = SERIES_SLOTS, valueOf, labelOf }) {
  if (items.length <= limit) return { kept: items, other: null };
  const kept = items.slice(0, limit - 1);
  const tail = items.slice(limit - 1);
  const total = tail.reduce((sum, item) => sum + BigInt(valueOf(item) || '0'), 0n);
  return {
    kept,
    other: {
      label: `Other (${tail.length})`,
      value: total.toString(),
      members: tail.map(labelOf),
    },
  };
}

/** Ratio of an exact integer string to a total, as a JS number in 0..1.
 *  Safe because it is used only for geometry (arc sweep, bar width), never for
 *  a displayed figure — displayed percentages come from the API. */
export function geometryRatio(value, total) {
  const t = BigInt(total || '0');
  if (t === 0n) return 0;
  const v = BigInt(value || '0');
  // Scale into a 1e9 integer window first so precision survives the divide.
  return Number((v * 1000000000n) / t) / 1e9;
}

/** Nice round axis ticks spanning 0..max, as JS numbers (axis labels only). */
export function axisTicks(max, count = 5) {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const rawStep = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceStep =
    (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10) *
    magnitude;
  const ticks = [];
  for (let value = 0; value <= max + niceStep * 0.001; value += niceStep) ticks.push(value);
  return ticks;
}

/** Shared geometry constants, matching the mark specs. */
export const MARK = {
  lineWidth: 2,
  markerRadius: 4.5, // ≥ 8px diameter
  cornerRadius: 4,
  surfaceGap: 2, // gap between adjacent fills, drawn in the surface colour
  ringWidth: 2, // surface ring on overlapping marks
};
